import { getEnv, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { v4 as nanoid } from 'uuid'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { ChatKind, MessageContractModel, MessageContractModelSelector } from '../api-store'
import { InputChannelSubscription } from '../api-store/RootStore.base'
import { withRootStore } from '../extensions'
import { RootStoreEnv } from '../RootStoreEnv'
import { SNACK_TYPES } from '../../navigation/toast'
import { Platform } from 'react-native'
import { WebSocketConnectionState } from '../network/SubscriptionClient'
import { getSubscriptionClientOptions } from '../../utils'
import { toastNotificationStore } from '../../components/ANewDesign/ToastNotification/ToastNotification'

const logger = extendLogger(LoggerContexts.Messages)

const localConnectionId = nanoid()
export let LocalConnectionId = `userId-${localConnectionId}`

export enum APIEvents {
  GameSnapshotForPlayer = 'api:user:game:snapshot:for:player',
  GameDeleted = 'api:user:game:deleted',
  GamePlayerLeft = 'api:user:game:left',
  GamePlayerJoined = 'api:user:joined:channel',
  AgoraUserUpdate = 'agora:user:update'
}

const SnackPayloadAllIn = types.model({
  type: types.literal(SNACK_TYPES.AllIn),
  userName: types.string,
  amount: types.number
})

const SnackConfigModel = types.model('SnackConfigModel').props({
  type: types.enumeration(Object.values(SNACK_TYPES)),
  seconds: types.number,
  showSeconds: types.optional(types.boolean, false),
  payload: types.union(SnackPayloadAllIn, types.undefined)
})

export type SnackConfig = Instance<typeof SnackConfigModel>

/**
 * A MessagesStore model.
 */

export const MessagesModel = types
  .model('MessagesModel')
  .props({
    snack: types.maybe(SnackConfigModel),
    messages: types.optional(types.array(MessageContractModel), []),
    isSortingMessageByTimestamp: types.optional(types.boolean, true)
  })
  .extend(withRootStore)
  .volatile((self) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const snackCallback = () => {}
    const unsubscribeOnReconnected = () => {
      return
    }
    const unsubscribeOnError = () => {
      return
    }
    return {
      snackCallback,
      unsubscribeOnError,
      unsubscribeOnReconnected
    }
  })
  .actions((self) => ({
    setUnsubscribeOnReconnected(unsubscribeOnReconnected: () => void) {
      self.unsubscribeOnReconnected = unsubscribeOnReconnected
    },
    clearMessages() {
      // self.messages.clear()
      self.messages.replace([])
    },
    setSnack(config: SnackConfig, cb?: () => void) {
      if (cb) self.snackCallback = cb
      self.snack = config
    },
    clearSnack() {
      self.snack = undefined
    },
    setIsSortingMessageByTimestamp(value: boolean) {
      self.isSortingMessageByTimestamp = value
    },
    onMessageData(message: MessageContractModelSelector) {
      // logger.debug('onMessageData incoming message', message)

      const isMessage = MessageContractModel.is(message)
      const env = getEnv<RootStoreEnv>(self)

      logger.info({
        message: 'Active websocket',
        socketBufferAmmount: env.graphqlWsClient.socket?.bufferedAmount
      })
      // logger.debug('onMessageData message correlationId', message.correlationId)
      // logger.debug('onMessageData message messageId', message.messageId)
      // logger.debug('onMessageData message payload', message.payload)
      if (isMessage) {
        const model = MessageContractModel.create(message)
        const currentChannelName = self.rootStore.communication.channel?.channelName ?? 'current'
        const incomingChannelName = model.channelName ?? 'incoming'
        const isRegisteredToCurrentChannel = incomingChannelName.toLowerCase() === currentChannelName.toLowerCase()
        // logger.debug('onMessageData message isRegisteredToCurrentChannel', isRegisteredToCurrentChannel)
        if (!isRegisteredToCurrentChannel) {
          logger.warn('onMessageData message isRegisteredToCurrentChannel', isRegisteredToCurrentChannel)
          return
        }
        // self.messages.push(model)
        self.messages.replace([...self.messages.slice(-20), model])
        // logger.log('onMessageData messages.length', self.messages.length)
        const ChatEventTypes = [
          ChatKind.admin,
          ChatKind.dealer,
          ChatKind.engine,
          ChatKind.observer,
          ChatKind.user,
          ChatKind.typing
        ]
        const kind = message?.payload?.kind ?? undefined
        // ******* increment a local version number for SnapshotViewIOS.....
        // if ([GameEventKind.Pause, GameEventKind.Start].includes(kind)) {
        //   if (model.payload?.version === undefined) {
        //     model = MessageContractModel.create({ ...message, payload: { ...message.payload, version: 0 } })
        //   }
        // }
        // logger.log('@@@@@@@@@@@@@@: ', kind, '==', JSON.stringify(message?.payload), JSON.stringify(message))
        // DEBUG: show incoming event in snackbar
        // if (kind) {
        //   self.snack = kind
        // }
        if (kind === 'celebration') {
          //@ts-ignore
          self.rootStore.communication.handleCelebrations({ ...model })
        } else if (kind === 'showCards') {
          //@ts-ignore
          self.rootStore.communication.handleShowCards({ ...model })
        } else if (kind === 'ToastNotification') {
          toastNotificationStore.getState().show({ text: model.payload.message })
        } else if (ChatEventTypes.includes(kind)) {
          //Todo: Check the attributes and update
          //@ts-ignore
          self.rootStore.communication.handleChat({ ...model })
        } else if (self.rootStore.game.appState === 'active' || Platform.OS === 'web') {
          //HACK: on web we are not getting appState correctly
          self.rootStore.game.events.pushGameEvents([{ message: { ...model } }])
        }
      } else {
        logger.warn('onMessageData message isMessage', isMessage)
      }
      return message
    },
    async unsubscribeAll() {
      logger.debug('unsubscribeAll')

      self.unsubscribeOnError()
      self.unsubscribeOnReconnected()
      // Reference:
      //   https://github.com/apollographql/apollo-client/issues/3967
      // const env = getEnv<RootStoreEnv>(self)
      // env.gqlWsClient.client.dispose()
      // env.gqlWsClient.unsubscribeAll()
      // logger.log('unsubscribeAll wsReadyState', self.rootStore.session.wsReadyState)
      // if (env.gqlWsClient.status === 3) {
      //   logger.log('unsubscribeAll already closed')
      // } else {
      //   logger.warn('unsubscribeAll force close and reconnect??')
      //   // env.gqlWsClient.close(false, false)
      // }
    }
  }))
  .actions((self) => ({
    async createChannelSubscription(): Promise<boolean> {
      logger.debug('createChannelSubscription isWebSocketConnected', self.rootStore.network.isWebSocketConnected)
      try {
        // const env = getEnv<RootStoreEnv>(self)
        // const manager = env.gqlWsClient

        if (Platform.OS === 'web') {
          self.rootStore?.network.setWebSocketState(WebSocketConnectionState.closed)
          const {
            env: { graphqlWsClient }
          } = await getSubscriptionClientOptions(self.rootStore)
          //@ts-ignore
          self.rootStore.api.setGraphqlWsClient(graphqlWsClient)
        }

        const channelName = self.rootStore.communication.channel?.channelName
        if (!channelName) {
          logger.warn('createChannelSubscription no channel, no-op', channelName)
          return false
          // throw new Error('Cannot create subscribe to channel')
        }

        // const env = getEnv<RootStoreEnv>(self)
        // const webSocketClient = env.gqlWsClient || env.graphqlWsClient
        // if (webSocketClient.isSocketConnected()) {
        //   logger.log('createChannelSubscription isSocketConnected', webSocketClient.readyState)
        //   return false
        // }
        self.unsubscribeAll()
        // DEPRECATED: we do this in subscribeMessages now
        // await self.rootStore.communication.joinChannel(channelName)
        const userId = self.rootStore.session.user?._id.toString()
        logger.info('createSubscription', channelName, userId)
        if (channelName && userId) {
          LocalConnectionId = `${userId}-${localConnectionId}`
          const connectionId = self.rootStore.environment.isLocal ? LocalConnectionId : undefined
          logger.info('createSubscription connectionId', connectionId)
          const channelSubscription: InputChannelSubscription = {
            connectionId,
            channelName,
            userId
          }
          logger.info('subscribeMessages', channelSubscription)
          if (self.rootStore.environment.isLocal && connectionId) {
            const env = getEnv<RootStoreEnv>(self)
            env.gqlHttpClient.setHeader('x-aws-connection-id', connectionId)
            await self.rootStore.api.mutateConnect({ connectionId }, undefined)
            await self.rootStore.api.mutateConnectUser({ connectionId }, undefined)
          }
          const unsubscribeOnReconnected = self.rootStore.api.subscribeMessages(
            { channelSubscription },
            undefined,
            self.onMessageData,
            (error: Error) => {
              logger.error('subscribeMessages error', typeof error, error)
            }
          )
          logger.info('subscribeMessages unsubscribeOnReconnected', unsubscribeOnReconnected)
          logger.info('subscribeMessages unsubscribeOnReconnected', typeof unsubscribeOnReconnected)
          self.setUnsubscribeOnReconnected(unsubscribeOnReconnected)
        }

        return true
      } catch (error) {
        // waitForConnection()
        logger.error('error', error)
        throw error
      }
    }
  }))
  .actions((self) => ({
    async unsubscribeGraphQLSubscription(dumpEvents: boolean) {
      await self.unsubscribeAll()
      if (dumpEvents) {
        self.clearMessages()
      }
    }
  }))
  .actions((self) => {
    const afterAttach = () => {
      logger.debug('afterAttach')
      // self.unsubscribeAll()
    }
    const afterCreate = () => {
      logger.debug('afterCreate')
      self.clearMessages()
    }
    return { afterAttach, afterCreate }
  })

/**
 * The MessagesStore instance.
 */
export type MessagesStore = Instance<typeof MessagesModel>

/**
 * The data of a MessagesStore.
 */
export type MessagesStoreSnapshot = SnapshotOut<typeof MessagesModel>
